import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {useSelector, useDispatch} from 'react-redux';
import {Modal, Button, Form, FormControl, Row, Col} from 'react-bootstrap';
import {css} from 'aphrodite';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faSpinner} from '@fortawesome/fontawesome-pro-solid';
import Markdown from 'components/Markdown';
import {login, loginError, loginResetAlert} from 'actions/login';
import {LOGIN_TYPE_NPI} from 'actions/login';
import {createGlobalErrorObj} from 'libs/react-widgets';
import {getError, getShowErrorAlert, getInProgress} from 'selectors/login';
import {getSettings} from 'selectors/settings';
import {getResolvedLoginSettings} from 'services/authentication';
import {getErrorField} from 'libs/react-widgets';
import ErrorAlert from 'components/ErrorAlert';

/**
 * NPI Login Modal Component
 * Shows NPI login form inside modal (for npiModal type workflow)
 *
 * @param {Object} props
 * @param {Function} props.onSuccess - Handler called after successful login
 * @param {Function} props.onCancel - Handler called when cancel button is clicked (returns to HCP verification modal)
 * @param {Function} props.onReject - Handler called when reject button is clicked (triggers redirect modal)
 * @param {Object} props.styleSheet - Aphrodite styleSheet object
 * @param {boolean} props.show - Whether to show the modal
 */
const NpiLoginModal = ({onSuccess = null, onCancel = null, onReject = null, styleSheet, show = true}) => {
  const dispatch = useDispatch();
  const settings = useSelector(getSettings);
  const error = useSelector(getError);
  const showErrorAlert = useSelector(getShowErrorAlert);
  const inProgress = useSelector(getInProgress);
  const authenticated = useSelector((state) => state.login.get('authenticated'));

  const [npiValue, setNpiValue] = useState('');

  // Check if authenticated after login
  React.useEffect(() => {
    if (authenticated && show) {
      if (onSuccess) {
        onSuccess();
      }
    }
  }, [authenticated, show, onSuccess]);

  const loginSettings = getResolvedLoginSettings(settings);

  const handleSubmit = (e) => {
    e.preventDefault();
    const npi = npiValue.trim();

    if (npi.match(/^\d{10}$/)) {
      dispatch(login(
        LOGIN_TYPE_NPI,
        npi,
        loginSettings.error.apiUnavailable,
        loginSettings.error.npiIncorrect
      ));
    } else {
      dispatch(loginError({
        response: createGlobalErrorObj([loginSettings.error.badNpi])
      }));
    }
  };

  const browser = typeof window !== 'undefined' ? window.navigator.userAgent : '';
  const isEdge = browser.includes('Edge');

  const buttonLayout = settings.loginV2?.meta?.npiModal?.buttonLayout || 'horizontal';
  const layoutClass = buttonLayout === 'vertical' || buttonLayout === 'column' ? 'npi-buttons-vertical' : '';

  // Combine reject/cancel handlers - only one will be present at a time
  const handleRejectOrCancel = onReject || onCancel;
  const rejectButtonLabel = settings.loginV2?.meta?.npiModal?.rejectButton ||
    (onReject ? 'I am not a Healthcare Provider' : 'Cancel');

  return (
    <Modal
      data-testid="npi-login-modal"
      show={show}
      backdrop="static"
      aria-labelledby="npi-login-modal-title"
      backdropClassName={css(styleSheet.modalBackdrop)}
      dialogClassName="pal-modal npi-login-modal"
      className={css(styleSheet.modal)}
    >
      <Modal.Header closeButton={false}>
        <Modal.Title id="npi-login-modal-title" className="modal-title npi-login-modal-title">
          {settings.loginV2?.meta?.npiModal?.instruction?.markdown ? (
            <Markdown>{settings.loginV2.meta.npiModal.instruction.markdown}</Markdown>
          ) : (
            settings.content.physicianVerificationPopup
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="modal-body npi-login-modal-body">
        <ErrorAlert
          error={getErrorField('global', 'global', error)}
          alertVisible={showErrorAlert}
          handleDismissAlert={() => dispatch(loginResetAlert())}
        />
        <Form onSubmit={handleSubmit}>
          <Form.Group className={`mb-3 ${layoutClass}`}>
            <Row className="g-2">
              <Col>
                <label htmlFor="npiNumber" className="sr-only">NPI Number</label>
                <FormControl
                  id="npiNumber"
                  name="npiNumber"
                  type="text"
                  required
                  aria-required="true"
                  placeholder="Please Enter NPI Number"
                  value={npiValue}
                  onChange={(e) => setNpiValue(e.target.value)}
                  autoComplete={isEdge ? 'off' : 'on'}
                  isInvalid={!!error}
                  disabled={inProgress}
                />
              </Col>
              <Col xs="auto">
                <Button
                  type="submit"
                  disabled={inProgress}
                  aria-label="Submit NPI number"
                  className={`${css(styleSheet.buttonPrimary, styleSheet.loginButton)} login-button`}
                >
                  {inProgress && (
                    <FontAwesomeIcon icon={faSpinner} spin size="lg" className="me-2" />
                  )}
                  {settings.loginV2?.meta?.npiModal?.loginButton || loginSettings.buttonLabel || 'Login'}
                </Button>
                {!layoutClass && handleRejectOrCancel && (
                  <Button
                    type="button"
                    disabled={inProgress}
                    onClick={handleRejectOrCancel}
                    className={`${css(styleSheet.buttonSecondary, styleSheet.modalReject)} me-2 reject-button`}
                  >
                    {rejectButtonLabel}
                  </Button>
                )}
              </Col>
            </Row>
            {layoutClass && handleRejectOrCancel && (
              <Row className="g-2 mt-2">
                <Col>
                  <Button
                    type="button"
                    disabled={inProgress}
                    onClick={handleRejectOrCancel}
                    className={`${css(styleSheet.buttonSecondary, styleSheet.modalReject)} reject-button w-100`}
                  >
                    {rejectButtonLabel}
                  </Button>
                </Col>
              </Row>
            )}
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer className="modal-footer npi-login-modal-footer">
        {/* Empty footer - buttons hidden as per requirement */}
      </Modal.Footer>
    </Modal>
  );
};

NpiLoginModal.propTypes = {
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func,
  onReject: PropTypes.func,
  styleSheet: PropTypes.object.isRequired,
  show: PropTypes.bool
};

export default NpiLoginModal;
